/**
 * The allow-listed sorts (and the order without a `?sort=`) of the admin lists (R1-09d): `?sort=<field>:<asc|desc>`. Zod-free, so browser
 * code reads them through `@kopi/contracts/client`; each list's query schema validates against them.
 */
export type SortDirection = "asc" | "desc";

/** A `?sort=` value over `F`. */
export type SortValue<F extends string> = `${F}:${SortDirection}`;

/** A `?sort=` value split into its field and direction. */
export function parseSort<F extends string>(sort: SortValue<F>): { field: F; direction: SortDirection } {
  const at = sort.lastIndexOf(":");
  return { field: sort.slice(0, at) as F, direction: sort.slice(at + 1) as SortDirection };
}

/** The `?sort=` value of a field and direction. */
export const sortValue = <F extends string>(field: F, direction: SortDirection): SortValue<F> =>
  `${field}:${direction}`;

/** `GET /v1/employees`: by name, or active staff first and then by name. */
export const EMPLOYEE_SORTS = ["name", "status"] as const;
export const DEFAULT_EMPLOYEE_SORT: SortValue<(typeof EMPLOYEE_SORTS)[number]> = "name:asc";
/** `GET /v1/products`: by name, by category name then name, or active first and then by name. */
export const PRODUCT_SORTS = ["name", "category", "status"] as const;
export const DEFAULT_PRODUCT_SORT: SortValue<(typeof PRODUCT_SORTS)[number]> = "name:asc";
/** `GET /v1/outlets/:outletId/devices`: by enrollment time. */
export const DEVICE_SORTS = ["enrolledAt"] as const;
export const DEFAULT_DEVICE_SORT: SortValue<(typeof DEVICE_SORTS)[number]> = "enrolledAt:desc";
/** `GET /v1/audit-events`: by time. */
export const AUDIT_SORTS = ["occurredAt"] as const;
export const DEFAULT_AUDIT_SORT: SortValue<(typeof AUDIT_SORTS)[number]> = "occurredAt:desc";
/** `GET /v1/customers`: by name, or by when the customer was added. */
export const CUSTOMER_SORTS = ["name", "createdAt"] as const;
export const DEFAULT_CUSTOMER_SORT: SortValue<(typeof CUSTOMER_SORTS)[number]> = "name:asc";
/** `GET /v1/categories`: the menu order, by name, or active first and then by name. */
export const CATEGORY_SORTS = ["position", "name", "status"] as const;
export const DEFAULT_CATEGORY_SORT: SortValue<(typeof CATEGORY_SORTS)[number]> = "position:asc";
/** `GET /v1/discounts`: newest first, by name, or active first and then by name. */
export const DISCOUNT_SORTS = ["createdAt", "name", "status"] as const;
export const DEFAULT_DISCOUNT_SORT: SortValue<(typeof DISCOUNT_SORTS)[number]> = "createdAt:desc";
/** `GET /v1/outlets/:outletId/stock/balances`: by ingredient name. */
export const STOCK_BALANCE_SORTS = ["ingredient"] as const;
export const DEFAULT_STOCK_BALANCE_SORT: SortValue<(typeof STOCK_BALANCE_SORTS)[number]> = "ingredient:asc";
/** `GET /v1/outlets/:outletId/refunds`: by request time, or by amount. */
export const REFUND_SORTS = ["requested", "amount"] as const;
export const DEFAULT_REFUND_SORT: SortValue<(typeof REFUND_SORTS)[number]> = "requested:desc";
/** `GET /v1/outlets/:outletId/shifts`: by opening time. */
export const SHIFT_SORTS = ["opened"] as const;
export const DEFAULT_SHIFT_SORT: SortValue<(typeof SHIFT_SORTS)[number]> = "opened:desc";
